'use client';

import { GridColDef } from '@mui/x-data-grid/models';
import { useTranslations } from 'next-intl';

import { RecapAsset } from '@/api/trading/types';
import { useRecap } from '@/api/trading/useRecap';
import { ApiPlatform } from '@/api/types';
import { AnimatedText } from '@/blocks/animations/animated-text';
import { Container } from '@/blocks/components/Container';
import { AssetSearch, assetSearchCentered } from '@/blocks/components/Controls/Autocomplete/AssetSearch';
import { DataGridWrapper, useColumnsVisibilityModel, useRowClick } from '@/blocks/components/DataGrid';
import {
  getColDef24hChange,
  getColDefCta,
  getColDefMarket,
  getColDefMarketCap,
  getColDefPrice,
  getColDefPriceAndChange,
  getColDefTrend,
  getColDefVol24,
} from '@/blocks/components/DataGrid/Asset/columnsDefenitions';
import { AssetGridRowModel, getAssetRowModel } from '@/blocks/components/DataGrid/Asset/rowModel';
import { AccentFragment } from '@/blocks/components/HeadingAccentFragment';
import { useSignup } from '@/blocks/components/PlatformLink';
import { SectionsGroup } from '@/blocks/components/SectionsGroup';
import { CenterHeading, VStack } from '@/blocks/components/layout';
import { EventBlockNames } from '@/providers/analytics/types';
import typography from '@/styles/typography.module.scss';

export function CryptoFuturesMarketsTableSection({ initialData }: { initialData: RecapAsset[] | null }) {
  const tr = useTranslations();
  const { markets, error, loading } = useRecap(ApiPlatform.FX, initialData);
  const { openSignupOnRowClick } = useRowClick();
  const { openSignupWithAsset } = useSignup();

  const columnsVisibilityModel = useColumnsVisibilityModel({
    mobile: ['name', 'priceAndChange'],
    tablet: ['name', 'price', 'vol24', 'change24h', 'marketcap'],
    desktop: ['name', 'price', 'vol24', 'change24h', 'marketcap', 'trendChart', 'cta'],
  });

  if (!markets) {
    return null;
  }

  const rows = markets.map(getAssetRowModel);

  const columns: GridColDef<AssetGridRowModel>[] = [
    getColDefMarket(tr('pages.cryptoFuturesMarkets.sections.markets.table.headers.market')),
    getColDefPriceAndChange(tr('pages.cryptoFuturesMarkets.sections.markets.table.headers.price')),
    getColDefPrice(tr('pages.cryptoFuturesMarkets.sections.markets.table.headers.lastPrice')),
    getColDefVol24(tr('pages.cryptoFuturesMarkets.sections.markets.table.headers.24hVolume')),
    getColDef24hChange(tr('pages.cryptoFuturesMarkets.sections.markets.table.headers.24hChange')),
    getColDefMarketCap(tr('pages.cryptoFuturesMarkets.sections.markets.table.headers.marketCap')),
    getColDefTrend(tr('pages.cryptoFuturesMarkets.sections.markets.table.headers.trend')),
    getColDefCta({
      text: tr('pages.cryptoFuturesMarkets.sections.markets.table.rowCta'),
      enText: tr('en.pages.cryptoFuturesMarkets.sections.markets.table.rowCta'),
    }),
  ];

  return (
    <SectionsGroup>
      <Container>
        <VStack data-event-block-name={EventBlockNames.assets_table}>
          <CenterHeading>
            <AnimatedText el={'h2'} className={typography.h2}>
              {tr.rich('pages.cryptoFuturesMarkets.sections.markets.title', {
                span: (_) => <AccentFragment>{_}</AccentFragment>,
              })}
            </AnimatedText>
          </CenterHeading>
          <AssetSearch
            className={assetSearchCentered}
            onChange={(_, option) => openSignupWithAsset(option)}
            platform={ApiPlatform.FX}
          />
          <DataGridWrapper
            loading={loading}
            columns={columns}
            rows={rows}
            onRowClick={openSignupOnRowClick}
            columnVisibilityModel={columnsVisibilityModel}
          />
        </VStack>
      </Container>
    </SectionsGroup>
  );
}
